import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toastPositions } from '@/feature/alerts/components/Toast';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { useUpdateUserInformation } from '@/feature/kyc/hooks/use-update-user-information';
import { useRootNavigation } from '@/hooks/use-root-navigation';
import { useUserStore } from '@/hooks/use-user-store';
import { useVerificationCode } from '@/hooks/use-verification-code';
import { resetToAccountScreen } from '@/navigation/navigation';
import { AccountEditStackScreenProps } from '@/navigation/types';

import VerificationScreenTemplate from './VerificationScreenTemplate';

export type EmailUpdateConfirmScreenProps = AccountEditStackScreenProps<'EmailUpdateConfirm'>;

export const EmailUpdateConfirm = ({ route }: EmailUpdateConfirmScreenProps) => {
    const [isResendRequested, setIsResendRequested] = useState(false);
    const rootNavigation = useRootNavigation();
    const { t } = useTranslation(['account', 'common']);
    const { showToast } = useAlerts();
    const { mutateAsync: updateUserInfo, isPending: isLoadingUpdateUserInfo } = useUpdateUserInformation();

    const { verificationCode, isSubmitting, errorMessage, onInputChange, setExternalError, setErrorMessage } =
        useVerificationCode(async () => {
            try {
                await updateUserInfo({
                    code: verificationCode,
                    address: {
                        email: route.params?.newEmail,
                    },
                });
            } catch {
                setErrorMessage(t('email_update_error'));
                return;
            }

            setErrorMessage('');
            const { actions, session } = useUserStore.getState();
            await actions.refresh(session); // Refreshing the user session to get the updated email.
            resetToAccountScreen(rootNavigation, 'AccountDetails');
            showToast({ message: t('your_email_has_been_updated'), position: toastPositions.TOP });
        });

    return (
        <VerificationScreenTemplate
            isLoading={isLoadingUpdateUserInfo || isSubmitting}
            errorMessage={errorMessage}
            verificationCode={verificationCode}
            isResendRequested={isResendRequested}
            setExternalError={setExternalError}
            onResendPress={() => {
                setIsResendRequested(true);
                setErrorMessage('');
            }}
            onInputChange={onInputChange}
        />
    );
};
